<template>
  <div id="index">
    <h1>PBP Stats</h1>
    <p><b>Stats from play by play data including:</b></p>
    <ul>
      <li>Actual Possession counts that exclude end of quarter possessions with heaves</li>
      <li>Points scored from assisted/unassisted 2 point and 3 point shots</li>
      <li>2 point and 3 point shooting fouls drawn and and 1s</li>
      <li>Rebound percentages broken down by shot zone</li>
      <li>Rebound counts that include team rebounds</li>
      <li>Blocked Shots broken down by shot zone</li>
      <li>Blocked Shots Recovered</li>
      <li>Assists broken down by shot zone</li>
      <li>Assist Points</li>
      <li>Putback points</li>
      <li>Live ball/dead ball turnover breakdown</li>
      <li>Foul types</li>
      <li>Seconds Per Possession Split by Offense and Defense</li>
      <li>Team Stats by Possession Start Type (Missed 2, Made 3, etc)</li>
      <li>Second Chance Stats</li>
      <li>Aggregate Stats Across Multiple Seasons</li>
      <li>Time and Score filtering for <router-link :to="{ name: 'wowy', params: { league: $parent.league }}">Wowy</router-link> queries</li>
      <li>Team and Player Stats by Number of Starters on Floor</li>
      <li>Expected eFG shot quality model based on location and play context. Click <a href="https://darrylblackport.com/posts/2018-09-03-pbp-shot-quality-model/" target="_blank">here</a> for more details</li>
      <li><router-link :to="{ name: 'live', params: { league: $parent.league }}">Live Stats During Games</router-link></li>
      <li><router-link :to="{ name: 'assist_combo_summary', params: { league: $parent.league }}">Assist Combos</router-link></li>
      <li>NBA Stats going back to 2000-01</li>
      <li>G-League Stats going back to 2016-17</li>
      <li>WNBA Stats going back to 2009</li>
      <li><a href="https://darrylblackport.com/posts/2019-04-03-why-pbpstats-possession-counts-lower/" target="_blank">Explanation for why possession counts are different than the NBA site</a></li>
      <li>All stats generated using <a href="https://github.com/dblackrun/pbpstats" target="_blank">pbpstats python package</a></li>
      <li><a href="https://api.pbpstats.com/docs" target="_blank">API Documentation</a></li>
    </ul>
    <h2>On/Off Stats</h2>
    <ul>
      <li class="link"><router-link :to="{ name: 'wowy', params: { league: $parent.league }}">Wowy</router-link></li>
      <li class="link"><router-link :to="{ name: 'wowy_combos', params: { league: $parent.league }}">Wowy Combinations</router-link></li>
      <li class="link"><router-link :to="{ name: 'on_off', params: { league: $parent.league, type: 'player' }}">On/Off - Player Stats</router-link></li>
      <li class="link"><router-link :to="{ name: 'on_off', params: { league: $parent.league, type: 'team' }}">On/Off - Team Stats - All Stats for Player</router-link></li>
      <li class="link"><router-link :to="{ name: 'on_off', params: { league: $parent.league, type: 'stat' }}">On/Off - Team Stats - All Players for Stat</router-link></li>
    </ul>
    <h2>Possession Finder</h2>
    <ul>
      <router-link :to="{ name: 'possession_finder', params: { league: $parent.league }}">Query For All Possessions by Start Type, Score, Time Remaining, Period</router-link>
    </ul>
    <h2>Totals and Per 100 Possession Stats</h2>
    <ul>
      <li class="link"><router-link :to="{ name: 'totals', params: { league: $parent.league, type: 'player' }}">Player</router-link></li>
      <li class="link"><router-link :to="{ name: 'totals', params: { league: $parent.league, type: 'team' }}">Team</router-link></li>
      <li class="link"><router-link :to="{ name: 'totals', params: { league: $parent.league, type: 'opponent' }}">Opponent</router-link></li>
      <li class="link"><router-link :to="{ name: 'totals', params: { league: $parent.league, type: 'lineup' }}">Lineup</router-link></li>
      <li class="link"><router-link :to="{ name: 'totals', params: { league: $parent.league, type: 'lineup-opponent' }}">Lineup Opponent</router-link></li>
    </ul>
    <h2>Game Logs</h2>
    <ul>
      <li class="link"><router-link :to="{ name: 'game_logs', params: { league: $parent.league, type: 'player' }}">Player</router-link></li>
      <li class="link"><router-link :to="{ name: 'game_logs', params: { league: $parent.league, type: 'team' }}">Team</router-link></li>
      <li class="link"><router-link :to="{ name: 'game_logs', params: { league: $parent.league, type: 'opponent' }}">Opponent</router-link></li>
    </ul>
    <h2>Visualizations</h2>
    <ul>
      <li class="link"><router-link  :to="{ name: 'assist_networks', params: { league: $parent.league, type: 'team' }}">Assist Networks</router-link></li>
      <li class="link"><router-link  :to="{ name: 'scatter_plots', params: { league: $parent.league }}">Scatter Plots</router-link ></li>
      <li class="link"><router-link  :to="{ name: 'year_over_year_plot', params: { league: $parent.league }}">League Year-Over-Year Plots</router-link ></li>
      <li class="link"><router-link  :to="{ name: 'score_margin', params: { league: $parent.league }}">Team Score Margin Frequencies</router-link ></li>
      <li class="link"><router-link  :to="{ name: 'four_factors_on_off', params: { league: $parent.league }}">Four Factors On-Off</router-link ></li>
      <li class="link"><router-link  :to="{ name: 'player_playing_time_distribution', params: { league: $parent.league }}">Player Playing Time Distribution By Game</router-link ></li>
      <li class="link"><router-link  :to="{ name: 'number_of_starter_stats_by_team', params: { league: $parent.league }}">Team Possessions By Number of Starters</router-link ></li>
      <li class="link"><router-link  :to="{ name: 'team_possession_length', params: { league: $parent.league }}">Possession Length Distribution By Team</router-link ></li>
      <li class="link"><router-link  :to="{ name: 'wowy_combo_playing_time_distribution', params: { league: $parent.league }}">Wowy Combo Playing Time Distribution By Game</router-link ></li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'Index',
  data () {
    return {
      msg: 'Welcome to Your Vue.js App'
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
h1, h2 {
  font-weight: normal;
}
ul {
  list-style-type: none;
  padding: 0;
}
li.link {
  display: inline-block;
  margin: 0 10px;
}
a {
  color: gray;
}
#index {
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  margin-top: 60px;
}
</style>



// WEBPACK FOOTER //
// src/components/Index.vue